import { Component, Input, OnDestroy, OnInit, Type } from '@angular/core';
import {
  AppFilter,
  AppTable,
  AppTableColumn,
  AuthPermissions,
  CommonDataModel,
  CustomComponentConfig,
  NestAudit,
  NestAuditEntityName
} from '../../_models/models';
import { handleExchangeTableSort } from '../../_services/exchange-table-sort.utils';
import { CreateQueryParams, RequestQueryBuilder, SFields } from '@libs/crud-request-nestjs';
import { cloneDeep } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ApiService } from '../../_services/api.service';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ExchangeAuditConfig {
  entityName: NestAuditEntityName;
  tableConfig: AppTable;
  tableColumns: AppTableColumn[];
  filters: AppFilter[];
  initialQueryConfig: CreateQueryParams;
  expandRowComponent?: Type<any>;
  entityIdParamName?: string;
  authPermissions?: AuthPermissions;
}

@Component({
  selector: 'app-exchange-audit',
  templateUrl: './exchange-audit.component.html',
  styleUrls: ['./exchange-audit.component.less']
})
export class ExchangeAuditComponent implements OnInit, OnDestroy {
  @Input() config: ExchangeAuditConfig;
  @Input() entityId?: string | number;

  auditTable: AppTable;
  auditFilters: AppFilter[];
  auditTableColumns: AppTableColumn[];
  auditQueryParams: CreateQueryParams;
  auditData$: Observable<CommonDataModel<NestAudit[]>>;
  isLoading = true;
  authPermissions: AuthPermissions;

  private expandOnInit: number = null;
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly apiService: ApiService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.initializeConfig();
    this.subscribeToRouteParams();
  }

  private initializeConfig(): void {
    if (!this.config) {
      throw new Error('ExchangeAuditComponent requires a config input');
    }

    this.auditTable = { ...this.config.tableConfig };
    this.auditFilters = this.config.filters;
    this.auditTableColumns = this.config.tableColumns;
    this.auditQueryParams = cloneDeep(this.config.initialQueryConfig);
    this.authPermissions = this.config.authPermissions || {
      canRead: true,
      canCreate: false,
      canUpdate: false,
      canDelete: false
    };
  }

  private subscribeToRouteParams(): void {
    if (this.entityId) {
      this.auditQueryParams.search['entityId'] = { $eq: this.entityId };
      this.loadAudits();
    } else if (this.config.entityIdParamName) {
      this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
        const id = params[this.config.entityIdParamName];
        this.auditQueryParams.search['entityId'] = { $eq: id };
        this.loadAudits();
      });
    } else {
      this.loadAudits();
    }
  }

  loadAudits(): void {
    const filters: string = RequestQueryBuilder.create(this.auditQueryParams).query();
    this.isLoading = true;
    this.auditData$ = this.apiService.getNestAudit(filters, this.config.entityName);
    this.auditData$ = this.auditData$.pipe(
      tap((res) => this.isLoading = res.isLoading),
      map((res) => this.mapAudits(res)));
  }

  onFiltersChange(event: { filters: AppFilter[]; filterId: string }): void {
    const changedFilter = event.filters.find(filter => filter.id === event.filterId);
    this.auditQueryParams.page = 1;

    switch (changedFilter.type) {
      case 'DATES_RANGE': {
        const startDateString = dayjs(changedFilter.selectedValues[0]).format('YYYY-MM-DD');
        const startDate = dayjs.tz(startDateString, 'America/New_York').startOf('day').toDate();

        const endDateString = dayjs(changedFilter.selectedValues[1]).format('YYYY-MM-DD');
        const endDate = dayjs.tz(endDateString, 'America/New_York').endOf('day').toDate();

        const filter = changedFilter.selectedValues
          ? { $between: [startDate, endDate] as any }
          : undefined;
        this.addFilter({ [changedFilter.prop]: filter });
        break;
      }
      case 'SEARCH': {
        const filter = changedFilter.selectedValues
          ? { $contL: '%' + changedFilter.selectedValues + '%' }
          : undefined;
        this.addFilter({ [changedFilter.prop]: filter });
        break;
      }
      case 'SELECT': {
        const filter = changedFilter.selectedValues?.length
          ? { $in: changedFilter.selectedValues.map(selectedValue => selectedValue.value) }
          : undefined;
        this.addFilter({ [changedFilter.prop]: filter });
        break;
      }
    }

    this.loadAudits();
  }


  onTablePageChange(table: AppTable) {
    this.auditQueryParams.page = table.pageIndex;
    this.auditQueryParams.limit = table.pageSize;
    this.loadAudits();
  }

  onTableSortChange(table: AppTable) {
    handleExchangeTableSort(table, {
      table: this.auditTable,
      queryParams: this.auditQueryParams,
      refreshCallback: () => this.loadAudits()
    });
  }


  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private addFilter(filter: SFields) {
    const [key, value] = Object.entries(filter)[0];
    if (value !== undefined) {
      this.auditQueryParams.search[key] = value;
    } else if (this.auditQueryParams.search[key]) {
      delete this.auditQueryParams.search[key];
    }
  }

  private mapAudits(audits: CommonDataModel<NestAudit[]>): CommonDataModel<NestAudit[]> {
    if (audits.data?.length) {
      audits.data = audits.data.map((originalAudit: NestAudit) => {
        const audit: NestAudit = cloneDeep(originalAudit);
        const expandRowComponentConfig = new CustomComponentConfig(
          this.config.expandRowComponent,
          { oldValue: audit.oldValue, newValue: audit.newValue }
        );
        return {
          ...audit,
          isExpanded: (this.expandOnInit === audit.id) || false,
          isExpandDisabled: false,
          expandRowComponent: expandRowComponentConfig,
        };
      });
      this.expandOnInit = null;
    }
    return audits;
  }
}
